import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from './api/client.js';
import { Entry } from './pages/Entry.js';
import { Connect } from './pages/Connect.js';
import { Dashboard } from './pages/Dashboard.js';
import { NewGeneration } from './pages/NewGeneration.js';
import { Result } from './pages/Result.js';
import { Gallery } from './pages/Gallery.js';
import { Feed } from './pages/Feed.js';
import { Leaderboard } from './pages/Leaderboard.js';
import { Buy } from './pages/Buy.js';
import { Remix } from './pages/Remix.js';
import { Drop } from './pages/Drop.js';
import { EarlyAccess } from './pages/EarlyAccess.js';
import { Privacy } from './pages/Privacy.js';
import { Terms } from './pages/Terms.js';
import { Layout } from './components/Layout.js';
import { GlobalSparkles } from './components/GlobalSparkles.js';
import { SignatureCursor } from './components/SignatureCursor.js';

/**
 * Spatial verification harnesses, driven by Playwright at pinned orbit poses
 * (tools/spatial2/verify) and by the spatial2 preview deploy. They exist only
 * under the Vite dev server or in a build made with VITE_TEST_ROUTES=1; a normal
 * production build leaves them out, so the live site has no bare debug pages.
 */
const TEST_ROUTES = import.meta.env.DEV || import.meta.env.VITE_TEST_ROUTES === '1';
const TEST_PAGES: { path: string; Page: LazyExoticComponent<ComponentType> }[] = TEST_ROUTES
  ? [
      {
        path: '/ldi-test',
        Page: lazy(() => import('./pages/LdiTest.js').then((m) => ({ default: m.LdiTest }))),
      },
      {
        path: '/spatial2-test',
        Page: lazy(() => import('./pages/Spatial2Test.js').then((m) => ({ default: m.Spatial2Test }))),
      },
      {
        path: '/softmpi-test',
        Page: lazy(() => import('./pages/SoftMpiTest.js').then((m) => ({ default: m.SoftMpiTest }))),
      },
    ]
  : [];

/**
 * Access model (public teaser + holder-gated full site):
 *   PUBLIC   — the splash → corridor (`/`) and the one free gild (`/new`) are open
 *              to anyone, no wallet. `/early-access` is the gate page below.
 *   GATED    — dashboard/gallery/feed/buy/etc. require a signed-in wallet that
 *              HOLDS a Whacky Whale (`<RequireHolder>`). A signed-in non-holder is
 *              sent to `/early-access`; a signed-out visitor is sent to `/` (the
 *              corridor, which carries the connect CTA).
 * The server enforces the same gate on the spend routes, so this is UX, not
 * security.
 */
function RequireHolder() {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 });
  if (meQuery.isLoading) return null; // Layout chrome shows; content resolves in a beat
  const me = meQuery.data;
  if (!me?.authenticated) return <Navigate to="/" replace />;
  if (me.holder !== true) return <Navigate to="/early-access" replace />;
  return <Outlet />;
}

export function App() {
  return (
    <>
      <GlobalSparkles />
      <SignatureCursor />
      <Routes>
        {/* Locked share page — rendered OUTSIDE Layout so there's no nav, no
            feed/gallery access; visitors can only remix this one piece. */}
        <Route path="/r/:id" element={<Drop />} />
        {/* Verification harnesses: dev server or VITE_TEST_ROUTES=1 builds only. */}
        {TEST_PAGES.map(({ path, Page }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={null}>
                <Page />
              </Suspense>
            }
          />
        ))}
        {/* Early-access gate — standalone (no app nav), like the splash cover. */}
        <Route path="/early-access" element={<EarlyAccess />} />

        <Route element={<Layout />}>
          {/* PUBLIC — no wallet required */}
          <Route path="/" element={<Entry />} />
          <Route path="/connect" element={<Connect />} />
          <Route path="/new" element={<NewGeneration />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* HOLDER-GATED — signed-in Whacky Whales holders only */}
          <Route element={<RequireHolder />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/result/:id" element={<Result />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/buy" element={<Buy />} />
            <Route path="/feed" element={<Feed />} />
            <Route path="/remix/:id" element={<Remix />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
          </Route>
        </Route>

        {/* Any unknown path (a typo, an old link, a stripped test page) goes home
            instead of rendering a blank page. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
